import { useState, useEffect } from "react";

// ─── Dark Mode Sync ───────────────────────────────────────────────────────────
// Reads the same state the Header writes:
//   localStorage["trw-dark-mode"] === "1" | "0"
//   document.documentElement classList "trw-dark-mode"
//   document.body classList "trw-dark-mode" | "dark"

function getHeaderDarkMode() {
  if (typeof window === "undefined") return false;
  const stored = window.localStorage.getItem("trw-dark-mode");
  if (stored === "1") return true;
  if (stored === "0") return false;
  return (
    document.documentElement.classList.contains("trw-dark-mode") ||
    document.body.classList.contains("trw-dark-mode") ||
    document.body.classList.contains("dark")
  );
}

function useSyncedDarkMode() {
  const [isDark, setIsDark] = useState(getHeaderDarkMode);

  useEffect(() => {
    const observer = new MutationObserver(() => setIsDark(getHeaderDarkMode()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class", "style"] });

    const onStorage = (e) => {
      if (e.key === "trw-dark-mode") setIsDark(e.newValue === "1");
    };
    window.addEventListener("storage", onStorage);

    return () => {
      observer.disconnect();
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  return isDark;
}

// ─── Rich text helper ─────────────────────────────────────────────────────────
// Instead of JSX fragments in module-level arrays (rejected by OXC),
// we store content as plain strings with a simple bold marker: **text**
// The Rich component renders them inline.

function Rich({ children }) {
  if (typeof children !== "string") return children;
  const parts = children.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith("**") && part.endsWith("**")) {
          return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        return part;
      })}
    </>
  );
}

// ─── Content Data (plain JS — no JSX) ────────────────────────────────────────

const sections = [
  {
    title: "Information We Collect and Its Purpose",
    paragraphs: ["We may collect the following types of information:"],
    bullets: [
      "**Personal Information:** such as your name, email address, or contact details, when you subscribe to our newsletter, leave comments, or contact us through forms.",
      "**Usage Data:** such as your IP address, browser type, device information, and time spent on pages, to help us improve site performance.",
      "**Preferences & Interactions:** such as liked, shared, or saved content, to personalize your browsing experience and recommendations.",
    ],
  },
  {
    title: "How We Use Your Information",
    paragraphs: ["We use collected information to:"],
    bullets: [
      "Deliver newsletters, blog updates, and responses to inquiries.",
      "Improve content quality and tailor it to reader interests.",
      "Enhance user experience and website functionality.",
      "Analyze site performance and audience engagement.",
      "Display **relevant travel-related promotions, affiliate offers, and advertisements.**",
    ],
  },
  {
    title: "Affiliate Disclosure & Advertising",
    paragraphs: [
      "Our website may contain **affiliate links** to travel services such as hotels, flights, tours, or booking platforms.",
    ],
    bullets: [
      "We may display **sponsored content or advertisements** from trusted partners in the travel industry. Such promotions help us keep our blog free for readers.",
      "While we aim to work only with reputable partners, we are not responsible for the content, policies, or practices of external websites linked from our blog.",
    ],
  },
  {
    title: "Sharing Your Information",
    paragraphs: [
      "We value your privacy and will **never sell or trade your personal information.** However, we may share data with:",
    ],
    bullets: [
      "Trusted third-party providers (hosting, analytics, email services) to operate and improve our website.",
      "Affiliate partners or advertising networks, solely for displaying relevant travel-related offers.",
    ],
  },
  {
    title: "Use of Cookies",
    paragraphs: ["Our website uses cookies to:"],
    bullets: [
      "Remember user preferences and settings.",
      "Analyze website traffic and optimize performance.",
      "Display relevant travel ads, affiliate offers, and recommendations.",
    ],
    extraParagraphs: [
      "You can control or disable cookies through your browser settings. Please note, disabling cookies may affect site functionality.",
    ],
  },
  {
    title: "External Links",
    paragraphs: [
      "Our site may include links to affiliate partners, travel booking platforms, or external resources. While we aim to link only to safe and reliable sites, we cannot guarantee their privacy practices. Clicking on such links is at your own discretion.",
    ],
  },
  {
    title: "Social Media",
    paragraphs: [
      "By engaging with our social media pages or sharing content through social features, you acknowledge that such activity may be visible to others.",
    ],
    extraParagraphs: [
      "We will never request sensitive details such as financial information, passwords, or OTPs through social media.",
    ],
  },
  {
    title: "Data Retention",
    paragraphs: [
      "We retain your personal information only as long as necessary for the purposes outlined in this policy, unless a longer retention period is required by law.",
    ],
  },
  {
    title: "Security",
    paragraphs: [
      "We take reasonable technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet can be guaranteed as 100% secure.",
    ],
  },
  {
    title: "Your Rights",
    paragraphs: ["Depending on your location, you may have the right to:"],
    bullets: [
      "Access, update, or correct your personal data.",
      "Request deletion of your data.",
      "Opt-out of marketing communications or affiliate promotions.",
      "Withdraw consent where previously provided.",
    ],
  },
  {
    title: "Updates to this Policy",
    paragraphs: [
      "We may update or modify this Privacy Policy at any time. Any changes will be posted on this page with the revised date. Please review this policy periodically to stay informed.",
    ],
    extraParagraphs: ["**Last Updated: 15.05.2026**"],
  },
  {
    title: "Contact Us",
    paragraphs: [
      "If you have any questions or concerns about this Privacy Policy or how your data is handled, please reach out to us at:",
    ],
    contactLines: [
      { label: "Legal Entity", value: "GD Brand Creative" },
      { label: "Brand Name", value: "Travel Rethink Ways" },
      { label: "Email", value: "travel@rethinkways.com", href: "mailto:travel@rethinkways.com" },
    ],
  },
];

// ─── Style helpers ────────────────────────────────────────────────────────────

const textPrimary = (dark) => ({ color: dark ? "#ffffff" : "rgb(13, 19, 23)" });
const textMuted   = (dark) => ({ color: dark ? "rgba(255,255,255,0.82)" : "rgba(13,19,23,0.76)" });

// ─── Component ────────────────────────────────────────────────────────────────

export default function PrivacyPolicy() {
  const isDark = useSyncedDarkMode();
  const container = { width: "100%", maxWidth: 1500, padding: "0 24px", margin: "0 auto" };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: isDark ? "#000000" : "#ffffff",
        color: isDark ? "#ffffff" : "rgb(13, 19, 23)",
        fontFamily: "'Poppins', Helvetica, Arial, sans-serif",
        WebkitFontSmoothing: "antialiased",
        textRendering: "optimizeLegibility",
        transition: "background 200ms, color 200ms",
      }}
    >
      {/* Inject fonts scoped to this component */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Yeseva+One&display=swap');
        .trw-pp-h1 {
          font-family: 'Yeseva One', Georgia, 'Times New Roman', serif !important;
          font-weight: 400 !important;
        }
      `}</style>

      <main style={{ padding: "40px 0 0" }}>

        {/* ── Hero ── */}
        <section style={container}>

          {/* Page title — Yeseva One */}
          <h1
            className="trw-pp-h1"
            style={{
              margin: 0,
              fontFamily: "'Yeseva One', Georgia, 'Times New Roman', serif",
              fontWeight: 400,
              fontSize: "41px",
              lineHeight: "51px",
              letterSpacing: 0,
              ...textPrimary(isDark),
            }}
          >
            Privacy Policy
          </h1>

          {/* Effective Date */}
          <p
            style={{
              margin: "22px 0 0",
              fontFamily: "'Poppins', Helvetica, Arial, sans-serif",
              fontWeight: 600,
              fontSize: "18px",
              lineHeight: "29px",
              ...textPrimary(isDark),
            }}
          >
            <strong style={{ fontWeight: 600 }}>Effective Date:</strong> 15.05.2026
          </p>

          {/* Intro paragraphs */}
          <div style={{ marginTop: "20px", display: "grid", gap: "22px", marginBottom: "36px" }}>
            {[
              "This Privacy Policy applies to **www.Travel.Rethinkways.com** (\"we,\" \"our,\" or \"us\") and governs how we collect, use, and protect your information when you visit or interact with our website.",
              "**Travel.Rethinkways.com** is powered by and legally operated by **GD Brand Creative**, which is responsible for managing this website and related services.",
              "By using this site, you consent to the practices described in this policy.",
            ].map((line, i) => (
              <p
                key={i}
                style={{
                  margin: 0,
                  fontWeight: 400,
                  fontSize: "17px",
                  lineHeight: "30px",
                  ...textMuted(isDark),
                }}
              >
                <Rich>{line}</Rich>
              </p>
            ))}
          </div>
        </section>

        {/* ── Article Sections ── */}
        <article style={container}>
          {sections.map((section, idx) => (
            <section
              key={idx}
              style={{
                borderTop: 0,
                marginTop: idx === 0 ? 0 : "36px",
              }}
            >
              {/* Section heading */}
              {section.title && (
                <h2
                  style={{
                    margin: "0 0 12px",
                    fontFamily: "'Poppins', Helvetica, Arial, sans-serif",
                    fontWeight: 600,
                    fontSize: "28px",
                    lineHeight: "39px",
                    ...textPrimary(isDark),
                  }}
                >
                  {section.title}
                </h2>
              )}

              {/* Paragraphs */}
              {section.paragraphs && section.paragraphs.map((para, i) => (
                <p
                  key={i}
                  style={{ margin: 0, fontSize: "17px", lineHeight: "30px", ...textMuted(isDark) }}
                >
                  <Rich>{para}</Rich>
                </p>
              ))}

              {/* Bullet list */}
              {section.bullets && section.bullets.length > 0 && (
                <ul style={{ margin: "8px 0 0 38px", padding: 0, listStyle: "disc" }}>
                  {section.bullets.map((bullet, i) => (
                    <li
                      key={i}
                      style={{
                        fontSize: "17px",
                        lineHeight: "30px",
                        marginTop: i > 0 ? "2px" : 0,
                        ...textMuted(isDark),
                      }}
                    >
                      <Rich>{bullet}</Rich>
                    </li>
                  ))}
                </ul>
              )}

              {/* Extra paragraphs */}
              {section.extraParagraphs && section.extraParagraphs.map((para, i) => (
                <p
                  key={i}
                  style={{
                    margin: "10px 0 0",
                    fontSize: "17px",
                    lineHeight: "30px",
                    ...textMuted(isDark),
                  }}
                >
                  <Rich>{para}</Rich>
                </p>
              ))}

              {/* Contact lines */}
              {section.contactLines && section.contactLines.length > 0 && (
                <div style={{ marginTop: "8px" }}>
                  {section.contactLines.map((line, i) => (
                    <p
                      key={i}
                      style={{ margin: 0, fontSize: "17px", lineHeight: "30px", ...textMuted(isDark) }}
                    >
                      <strong style={{ fontWeight: 600, ...textPrimary(isDark) }}>
                        {line.label}:
                      </strong>{" "}
                      {line.href ? (
                        <a
                          href={line.href}
                          style={{
                            textDecoration: "underline",
                            textUnderlineOffset: "3px",
                            ...textPrimary(isDark),
                          }}
                        >
                          {line.value}
                        </a>
                      ) : (
                        line.value
                      )}
                    </p>
                  ))}
                </div>
              )}
            </section>
          ))}
        </article>
      </main>
    </div>
  );
}
